import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { SpeechBubble } from '../components/ui/SpeechBubble';
import { FloatingDecor } from '../components/ui/FloatingDecor';
const sectionEntrance = {
  initial: {
    opacity: 0,
    y: 40,
    scale: 0.96
  },
  whileInView: {
    opacity: 1,
    y: 0,
    scale: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    type: 'spring',
    stiffness: 200,
    damping: 20
  }
};
const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemEntrance = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
const popWord = {
  initial: {
    opacity: 0,
    scale: 0.8,
    rotate: 0
  },
  animate: {
    opacity: 1,
    scale: 1,
    rotate: -2
  },
  transition: {
    type: 'spring',
    stiffness: 300,
    damping: 15,
    delay: 0.3
  }
};
const statBounce = {
  initial: {
    opacity: 0,
    scale: 0.5
  },
  whileInView: {
    opacity: 1,
    scale: 1
  },
  viewport: {
    once: true
  },
  transition: {
    type: 'spring',
    stiffness: 400,
    damping: 10
  }
};
export function Home() {
  return <div className="pt-24 md:pt-32 pb-0 overflow-hidden" data-id="element-207">
      {/* Hero Section - Cream Background */}
      <section className="container mx-auto px-6 md:px-12 mb-24 relative" data-id="element-208">
        <FloatingDecor color="pink" count={6} data-id="element-209" />
        <div className="flex flex-col lg:flex-row items-center gap-12 relative z-10" data-id="element-210">
          <motion.div className="flex-1 text-center lg:text-left" initial="initial" animate="animate" variants={stagger} data-id="element-211">
            <motion.div variants={itemEntrance} className="mb-8" data-id="element-212">
              <SpeechBubble text="AGENCE 360°" color="pink" data-id="element-213" />
            </motion.div>

            <motion.h1 variants={itemEntrance} className="text-6xl md:text-7xl lg:text-8xl leading-[1.1] mb-6 text-balance" data-id="element-214">
              Connecting{' '}
              <motion.span variants={popWord} className="inline-block text-jany-pink italic" data-id="element-215">
                talents.
              </motion.span>
              <br data-id="element-216" />
              Creating{' '}
              <motion.span variants={popWord} className="inline-block text-jany-orange" data-id="element-217">
                impact.
              </motion.span>
            </motion.h1>

            <motion.p variants={itemEntrance} className="text-lg md:text-xl text-jany-dark/80 mb-10 max-w-xl mx-auto lg:mx-0 font-medium" data-id="element-218">
              L'agence marketing 360° qui connecte talents, marques et contenus
              qui comptent — influence, sites web, formation et vidéo, sous une
              seule signature.
            </motion.p>

            <motion.div variants={itemEntrance} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-219">
              <Button href="/contact?type=brand" variant="pink" size="lg" className="w-full sm:w-auto" data-id="element-220">
                Vous êtes une marque
              </Button>
              <Button href="/contact?type=talent" variant="orange" size="lg" className="w-full sm:w-auto" data-id="element-221">
                Vous êtes un talent
              </Button>
            </motion.div>
          </motion.div>

          <motion.div className="flex-1 relative w-full max-w-lg lg:max-w-none" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-222">
            {/* Hero visuel : grand logo JANY & CO sur fond dégradé pastel, à la place d'une photo */}
            <div className="relative aspect-[4/5] rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white bg-gradient-to-br from-jany-pink-pale via-jany-cream to-jany-yellow/40 flex flex-col items-center justify-center" data-id="element-223">
              <FloatingDecor color="pink" count={10} data-id="element-224" />
              <motion.div animate={{
              rotate: [0, 8, 0, -8, 0]
            }} transition={{
              repeat: Infinity,
              duration: 6,
              ease: 'easeInOut'
            }} className="relative z-10" data-id="element-225">
                <Sparkles className="text-jany-yellow mx-auto mb-6" size={48} data-id="element-226" />
              </motion.div>
              <div className="relative z-10 text-center font-display leading-none" data-id="element-227">
                <div className="text-jany-pink text-7xl md:text-8xl" data-id="element-228">JANY</div>
                <div className="text-jany-orange text-6xl md:text-7xl -mt-2" data-id="element-229">
                  & CO
                </div>
              </div>
              <p className="relative z-10 text-jany-dark/60 text-xs md:text-sm font-bold tracking-[0.3em] uppercase mt-6" data-id="element-230">
                Talent & Influence Agency
              </p>
            </div>

            {/* Floating Badges */}
            <motion.div animate={{
            y: [0, -15, 0]
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: 'easeInOut'
          }} className="absolute -top-6 -right-6 md:-right-10" data-id="element-231">
              <Badge variant="yellow-star" size="xl" label="AGENCE" data-id="element-232" />
            </motion.div>
            <motion.div animate={{
            y: [0, 15, 0]
          }} transition={{
            repeat: Infinity,
            duration: 5,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -bottom-8 -left-6 md:-left-10" data-id="element-233">
              <Badge variant="rose-sparkle" size="xl" label="TALENTS" data-id="element-234" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Key Figures Marquee / Stats - Pink Background */}
      <motion.section {...sectionEntrance} className="bg-jany-pink text-white py-20 mb-0 rounded-t-[4rem] relative" data-id="element-235">
        <FloatingDecor color="white" count={10} data-id="element-236" />
        <div className="container mx-auto px-6 relative z-10" data-id="element-237">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-238">
            {[{
            num: '50+',
            label: 'Talents Exclusifs'
          }, {
            num: '200+',
            label: 'Campagnes Réalisées'
          }, {
            num: '15M',
            label: 'Reach Cumulé'
          }, {
            num: '4',
            label: 'Expertises Réunies'
          }].map((stat, i) => <motion.div key={i} variants={statBounce} initial="initial" whileInView="whileInView" viewport={{
            once: true
          }} transition={{
            ...statBounce.transition,
            delay: i * 0.1
          }} data-id="element-239">
                <div className="font-display text-6xl md:text-7xl mb-2" data-id="element-240">
                  {stat.num}
                </div>
                <div className="text-sm font-bold uppercase tracking-wider opacity-90" data-id="element-241">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </div>
      </motion.section>

      {/* Services Preview - Yellow Background - 4 Expertises */}
      <motion.section {...sectionEntrance} className="bg-jany-yellow py-24 mb-0 rounded-t-[4rem] -mt-8 relative z-10" data-id="element-242">
        <FloatingDecor color="dark" count={8} data-id="element-243" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-244">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-245">
            <SpeechBubble text="WHAT WE DO" color="pink" className="mb-6" data-id="element-246" />
            <h2 className="text-5xl md:text-7xl mb-6 text-jany-dark" data-id="element-247">
              Une agence,{' '}
              <motion.span variants={popWord} initial="initial" whileInView="animate" viewport={{
              once: true
            }} className="inline-block text-jany-pink italic" data-id="element-248">
                4 expertises
              </motion.span>
            </h2>
            <p className="text-xl text-jany-dark/80 font-medium" data-id="element-249">
              Influence, création de site web, formation et production vidéo
              — tout sous une seule signature, pour des projets cohérents de bout
              en bout.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-250">
            {[{
            title: 'JANY AGENCY',
            desc: 'Gestion de créateurs et campagnes sponsorisées, du casting au suivi de performance.',
            badge: 'rose-sparkle' as const
          }, {
            title: 'JANY WEBSITE',
            desc: 'Sites vitrine et e-commerce sur-mesure, pensés pour convertir.',
            badge: 'yellow-star' as const
          }, {
            title: 'JANY CONSULTING',
            desc: 'Formations pour créateurs et marques sur les métiers du digital.',
            badge: 'lightrose-eye' as const
          }, {
            title: 'JANY STUDIO',
            desc: 'Captation et montage de contenus et spots publicitaires qui captent l’attention.',
            badge: 'blue-palmtree' as const
          }].map((service, idx) => <motion.div key={idx} whileHover={{
            y: -10,
            scale: 1.02,
            rotate: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 15
          }} className="bg-white rounded-[3rem] p-8 shadow-lg border-4 border-white flex flex-col" data-id="element-251">
                <Badge variant={service.badge} size="lg" className="mb-6" data-id="element-252" />
                <h3 className="text-2xl mb-3 font-display text-jany-dark" data-id="element-253">
                  {service.title}
                </h3>
                <p className="text-jany-dark/70 mb-6 font-medium flex-1" data-id="element-254">
                  {service.desc}
                </p>
                <Button href="/services" variant="outline" size="sm" className="w-full" data-id="element-255">
                  En savoir plus <ArrowRight size={16} className="ml-2" data-id="element-256" />
                </Button>
              </motion.div>)}
          </div>
        </div>
      </motion.section>

      {/* Quote/Moodboard Section - Orange Background */}
      <motion.section {...sectionEntrance} className="bg-jany-orange py-32 mb-0 rounded-t-[4rem] -mt-8 relative z-20 overflow-hidden" data-id="element-257">
        <FloatingDecor color="white" count={12} data-id="element-258" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-259">
          <div className="max-w-5xl mx-auto text-center" data-id="element-260">
            <SpeechBubble text="CLIENT LOVE" color="yellow" className="mb-10" data-id="element-261" />
            <h2 className="text-5xl md:text-7xl lg:text-8xl leading-tight mb-10 text-white italic" data-id="element-262">
              "Good people. Great content. Real impact."
            </h2>
            <p className="text-2xl font-bold uppercase tracking-widest text-jany-yellow" data-id="element-263">
              — La philosophie Jany & Co
            </p>
          </div>
        </div>

        {/* Decorative Badges */}
        <motion.div animate={{
        rotate: 360
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute top-20 left-20 opacity-80" data-id="element-264">
          <Badge variant="blue-palmtree" size="xl" data-id="element-265" />
        </motion.div>
        <motion.div animate={{
        rotate: -360
      }} transition={{
        duration: 25,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute bottom-20 right-20 opacity-80" data-id="element-266">
          <Badge variant="rose-sparkle" size="xl" data-id="element-267" />
        </motion.div>
      </motion.section>

      {/* CTA Section - Blue Background */}
      <motion.section {...sectionEntrance} className="bg-jany-blue py-32 rounded-t-[4rem] -mt-8 relative z-30 text-center" data-id="element-268">
        <FloatingDecor color="dark" count={6} data-id="element-269" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-270">
          <motion.div whileHover={{
          scale: 1.02
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20
        }} className="max-w-3xl mx-auto bg-white rounded-[4rem] p-12 md:p-20 shadow-xl" data-id="element-271">
            <Badge variant="orange-ampersand" size="lg" className="mx-auto mb-8" data-id="element-272" />
            <h2 className="text-5xl md:text-6xl mb-6 text-jany-dark" data-id="element-273">
              Prêt à collaborer ?
            </h2>
            <p className="text-xl text-jany-dark/70 mb-10 font-medium" data-id="element-274">
              Que vous soyez une marque cherchant à rayonner ou un talent prêt à
              passer au niveau supérieur, discutons de votre projet.
            </p>
            <Button href="/contact" variant="pink" size="lg" className="text-xl px-10 py-5" data-id="element-275">
              Démarrer un projet
            </Button>
          </motion.div>
        </div>
      </motion.section>
    </div>;
}